import { useEffect } from 'react';

import { useConfiguration } from '../context/configurationContext';

type FeaturedListingsOptions = {
  featuredListings?: {
    onFetchFeaturedListings?: (
      sectionId: string,
      parentPage: string,
      listingImageConfig: any,
      allSections: any[]
    ) => void;
    getListingEntitiesById?: (listingIds: any[]) => any[];
    parentPage?: string;
    featuredListingData?: Record<string, {
      listingIds?: any[];
      fetched?: boolean;
      inProgress?: boolean;
      error?: any;
    }>;
  };
};

export type FeaturedListingsSectionResult = {
  listings: any[];
  fetched: boolean;
  inProgress: boolean;
  error: any;
};

/**
 * Wires a custom landing section to the shared `featuredListings` redux slice
 * (the same pipeline default Sharetribe `SectionListings` uses). Triggers the
 * fetch on mount and returns hydrated listing entities for the section.
 *
 * @param sectionId - The unique section id from PageBuilder
 * @param options - The `options` prop passed by SectionBuilder; carries the
 *   featuredListings wrapper.
 * @param allSections - All sections on the page (needed by the thunk to
 *   resolve cross-section selection types).
 */
export const useFeaturedListings = (
  sectionId: string,
  options: FeaturedListingsOptions | undefined,
  allSections: any[] | undefined
): FeaturedListingsSectionResult => {
  const config = useConfiguration() as any;
  const featuredListings = options?.featuredListings;
  const featuredListingData = featuredListings?.featuredListingData;
  const onFetchFeaturedListings = featuredListings?.onFetchFeaturedListings;
  const getListingEntitiesById = featuredListings?.getListingEntitiesById;
  const parentPage = featuredListings?.parentPage;

  const sectionState = featuredListingData?.[sectionId];
  const listingIds = sectionState?.listingIds || [];
  const fetched = !!sectionState?.fetched;
  const inProgress = !!sectionState?.inProgress;
  const error = sectionState?.error || null;

  const listingImageConfig = config?.layout?.listingImage;

  useEffect(() => {
    if (!fetched && !inProgress && !error && onFetchFeaturedListings && parentPage) {
      onFetchFeaturedListings(sectionId, parentPage, listingImageConfig, allSections || []);
    }
    // Mirror SectionListings: fire once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const listings =
    listingIds.length > 0 && getListingEntitiesById ? getListingEntitiesById(listingIds) : [];

  return { listings, fetched, inProgress, error };
};
